/**
 * Section templates — T3.14.
 *
 * Pre-built GridItem arrays for common page sections. Each template
 * is a function returning fresh items (new IDs on every call) so
 * multiple instances can be inserted without id collisions.
 *
 * The "Add section" button in PageNode calls addItem() for each item
 * in the chosen template via useUpdateGrid().
 *
 * T3.15 (actual starter templates as full Yjs blobs) is NOT_AGENT_WORK —
 * a designer will author richer templates via the editor. These section
 * templates are the building blocks; starter templates compose them.
 */
import type { GridItem } from "@fredericrous/grid/types";

export interface SectionTemplate {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly icon: string;
  /** Height of the section BLOCK in page-grid rows (≈ tallest widget
   *  bottom in the nested grid). The section's widgets live in a nested
   *  grid; this is the full-width block that hosts it. */
  readonly height: number;
  /** The section's widgets (relative coords, y=0-based) — they populate
   *  the section's NESTED grid, not the page grid directly. */
  readonly items: () => Array<Omit<GridItem, "id"> & { id: string }>;
  /** Props of the section BLOCK itself (fresh per call), e.g. the `form` of a
   *  section whose widgets submit together. */
  readonly sectionProps?: () => Record<string, unknown>;
}

let _counter = 0;
function uid(prefix: string) {
  return `${prefix}-${Date.now()}-${++_counter}`;
}

export const SECTION_TEMPLATES: SectionTemplate[] = [
  {
    id: "hero",
    label: "Hero",
    description: "Full-width heading + subtext",
    icon: "🦸",
    height: 5,
    items: () => [
      {
        id: uid("hero-title"),
        position: { x: 0, y: 0, w: 12, h: 3 },
        constraints: { minH: 2 },
        contentType: "text",
        contentProps: {
          lexicalState: JSON.stringify({
            root: {
              children: [
                {
                  children: [
                    {
                      detail: 0,
                      format: 0,
                      mode: "normal",
                      style: "",
                      text: "Your headline here",
                      type: "text",
                      version: 1,
                    },
                  ],
                  direction: "ltr",
                  format: "",
                  indent: 0,
                  type: "heading",
                  version: 1,
                  tag: "h1",
                },
              ],
              direction: "ltr",
              format: "",
              indent: 0,
              type: "root",
              version: 1,
            },
          }),
        },
      },
      {
        id: uid("hero-sub"),
        position: { x: 0, y: 3, w: 8, h: 2 },
        constraints: { minH: 1 },
        contentType: "text",
        contentProps: {},
      },
    ],
  },
  {
    id: "two-col",
    label: "Two columns",
    description: "Side-by-side text blocks",
    icon: "⬛⬛",
    height: 4,
    items: () => [
      {
        id: uid("two-col-left"),
        position: { x: 0, y: 0, w: 6, h: 4 },
        constraints: { minW: 3 },
        contentType: "text",
        contentProps: {},
      },
      {
        id: uid("two-col-right"),
        position: { x: 6, y: 0, w: 6, h: 4 },
        constraints: { minW: 3 },
        contentType: "text",
        contentProps: {},
      },
    ],
  },
  {
    id: "image-text",
    label: "Image + text",
    description: "Image on left, text on right",
    icon: "🖼️",
    height: 4,
    items: () => [
      {
        id: uid("img-text-img"),
        position: { x: 0, y: 0, w: 5, h: 4 },
        constraints: { minW: 3, minH: 3 },
        contentType: "image",
        contentProps: {},
      },
      {
        id: uid("img-text-txt"),
        position: { x: 5, y: 0, w: 7, h: 4 },
        constraints: { minW: 3 },
        contentType: "text",
        contentProps: {},
      },
    ],
  },
  {
    id: "three-features",
    label: "Three features",
    description: "Three equal-width feature cards",
    icon: "📦📦📦",
    height: 4,
    items: () => [
      {
        id: uid("feat-1"),
        position: { x: 0, y: 0, w: 4, h: 4 },
        constraints: { minH: 3 },
        contentType: "text",
        contentProps: {},
      },
      {
        id: uid("feat-2"),
        position: { x: 4, y: 0, w: 4, h: 4 },
        constraints: { minH: 3 },
        contentType: "text",
        contentProps: {},
      },
      {
        id: uid("feat-3"),
        position: { x: 8, y: 0, w: 4, h: 4 },
        constraints: { minH: 3 },
        contentType: "text",
        contentProps: {},
      },
    ],
  },
  {
    id: "contact-form",
    label: "Contact form",
    description: "Heading + contact form widget",
    icon: "📬",
    height: 8,
    items: () => [
      {
        id: uid("contact-heading"),
        position: { x: 0, y: 0, w: 12, h: 2 },
        constraints: { minH: 1 },
        contentType: "text",
        contentProps: {},
      },
      {
        id: uid("contact-form"),
        position: { x: 0, y: 2, w: 8, h: 6 },
        constraints: { minH: 4 },
        contentType: "form",
        contentProps: {
          fields: [
            { type: "text", name: "name", label: "Name", required: true },
            { type: "email", name: "email", label: "Email", required: true },
            { type: "textarea", name: "message", label: "Message", rows: 4 },
          ],
          submitButtonLabel: "Send",
        },
      },
    ],
  },
  {
    id: "search",
    label: "Search box",
    description: "A search field + button (sends visitors to Google)",
    icon: "🔎",
    height: 2,
    // The SECTION is the form: it owns where a search goes; the input only
    // names the query (`q`). Zero JavaScript on the published page.
    sectionProps: () => ({
      form: {
        id: uid("search-form"),
        action: {
          kind: "navigate",
          url: "https://www.google.com/search",
          method: "get",
        },
      },
    }),
    items: () => [
      {
        id: uid("search-input"),
        position: { x: 0, y: 0, w: 9, h: 1 },
        constraints: { minH: 1, minW: 3 },
        contentType: "input",
        contentProps: {
          name: "q",
          label: "Search",
          type: "search",
          icon: "search",
          placeholder: "Search",
        },
      },
      {
        id: uid("search-submit"),
        position: { x: 9, y: 0, w: 3, h: 1 },
        constraints: { minH: 1, minW: 2 },
        contentType: "button",
        contentProps: { label: "Search", type: "submit", variant: "primary" },
      },
    ],
  },
  {
    id: "heading",
    label: "Heading section",
    description: "New section with a themed heading",
    icon: "🔠",
    height: 2,
    items: () => [
      {
        id: uid("heading"),
        position: { x: 0, y: 0, w: 12, h: 2 },
        constraints: { minH: 1 },
        contentType: "heading",
        contentProps: { text: "Section heading", level: 2 },
      },
    ],
  },
  {
    id: "button",
    label: "Button section",
    description: "New section with a call-to-action button",
    icon: "🔘",
    height: 2,
    items: () => [
      {
        id: uid("button"),
        position: { x: 0, y: 0, w: 3, h: 2 },
        constraints: { minH: 1, minW: 2 },
        contentType: "button",
        contentProps: {
          label: "Get started",
          variant: "primary",
          size: "default",
        },
      },
    ],
  },
  {
    id: "card",
    label: "Card section",
    description: "New section with a content card",
    icon: "🪧",
    height: 4,
    items: () => [
      {
        id: uid("card"),
        position: { x: 0, y: 0, w: 5, h: 4 },
        constraints: { minH: 2, minW: 3 },
        contentType: "card",
        contentProps: {
          title: "Card title",
          body: "A short description goes here.",
          variant: "outlined",
        },
      },
    ],
  },
];
